"use client";

import React, { useContext, useMemo, forwardRef, useEffect, useRef } from "react"
import Icon from "./components/icon"
import { LayoutContext } from "../layout-context-container";
import Link from "next/link";
import { renderToString } from "react-dom/server";

function Button({
  type = undefined, // anchor (default), submit, button, tooltip
  variant = undefined, // no style (default), outline, solid
  iconType = undefined, // carat, carat-up, carat-down, download, external, loading
  iconImageProps = null,
  href = "",
  targetBlank = false,
  forceAnchorTag = false,
  externalLink = false, // for internal pages only
  leaveSiteLink = false, // for external pages only
  gtmUseLabelInsteadOfUrl = false,
  onClick = undefined,
  customClass = undefined,
  disabled = false,
  preventDefault = false,
  tooltipData = null,
  children,
  ...rest
}, ref) {
  const {setInterstitialStateOpen, setInterstitialHref, setInterstitialLeaveSite, setInterstitialActionLabel, tooltipRef} = useContext(LayoutContext);
  const buttonRef = useRef(null);

  const buttonClass = useMemo(() => {
    var className = 'button';

    if (type) {
      className = `${className} button--${type}`;
    }

    if (variant) {
      if (Array.isArray(variant)) {
        variant.forEach((vari) => {
          className = `${className} button--${vari}`;
        });
      } else {
        className = `${className} button--${variant}`;
      }
    }

    if (customClass) {
      className = `${className} ${customClass}`
    }

    return className;
  }, [customClass, type, variant]);

  useEffect(() => {
    if (buttonRef?.current) {
      buttonRef.current.tooltipData = tooltipData;
    }
  }, [tooltipData, buttonRef]);

  useEffect(() => {
    const button = buttonRef.current;
    function handleKeyEvent(e) {
      if (e.code === 'Space' && type === undefined) {
        e.preventDefault();
        buttonRef.current?.click();
      }
    }
    button?.addEventListener('keydown', handleKeyEvent);

    return () => button?.removeEventListener('keydown', handleKeyEvent);
  }, [type, buttonRef]);

  function handleClick(e) {
    if (href === "" || preventDefault || iconType === 'loading' || iconType === 'loading-eggplant' || type === 'tooltip') {
      e.preventDefault();
      e.stopPropagation();
    }
    if (type === "tooltip") {
      const target = e.currentTarget;
      const tooltipData = buttonRef?.current?.tooltipData;

      /*
        A tooltipData attribute must be provided with the following object structure

        data-tooltip={{
          heading: <>Heading</>,
          content: <>Content</>,
        }}
      */

      if (tooltipData && tooltipRef) {
        tooltipRef.current?.open({...tooltipData, target});
      }

      return;
    }

    if (externalLink || leaveSiteLink) {
      e.preventDefault();
      setInterstitialHref(href);
      setInterstitialActionLabel(gtmUseLabelInsteadOfUrl ? renderToString(children) : undefined)
      setInterstitialLeaveSite(leaveSiteLink);
      setInterstitialStateOpen(true);
    }
    if (onClick !== undefined) {
      onClick(e);
    }
  }

  if (type !== undefined || type === 'submit' || type === 'button' || type === 'tooltip') {
    // used external links that trigger interstitial modal and form submissions
    return (
      <button
        type={variant !== 'submit' ? 'button' : 'submit'}
        disabled={disabled}
        onClick={handleClick}
        className={buttonClass}
        ref={buttonRef}
        tabIndex={0}
        {...rest}
      >
        {children}
        <Icon type={iconType} imageProps={iconImageProps} />
      </button>
    )
  } else if (targetBlank === true || forceAnchorTag === true || leaveSiteLink) {
    // trusted page or pdf with target blank
    return (
      <a
        href={href}
        target={targetBlank ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={buttonClass}
        onClick={handleClick}
        ref={buttonRef}
        {...rest}
      >
        {children}
        <Icon type={iconType} imageProps={iconImageProps} />
      </a>
    )
  } else {
    // internal link
    return (
      <Link
        href={href}
        role="button"
        className={buttonClass}
        onClick={handleClick}
        ref={buttonRef}
        {...rest}
      >
        {children}
        <Icon type={iconType} imageProps={iconImageProps} />
      </Link>
    )
  }
}

export default forwardRef(Button)
